import { useEffect } from 'react';
import useSWR, { useSWRConfig, type SWRConfiguration } from 'swr';

import { buildQueryKey } from '@common/utils/buildQueryKey';

import {
  getFeed,
  type GetFeedParams,
  type GetFeedResponse,
} from '@services/FeedService';

export type UseGetFeedParams = {
  queryParams?: GetFeedParams;
  /**
   * Read when each request starts, and sent as its `limit`. It stays out of
   * the key: a new key reads as a filter change, which deletes the cache
   * entry and makes the list drop a page whose key moved, and the limit
   * changes with every swipe.
   */
  getLimit?: () => number;
  swrOptions?: SWRConfiguration<GetFeedResponse, unknown>;
};

export const getFeedQueryKey = (queryParams?: GetFeedParams) =>
  buildQueryKey('get-feed', queryParams);

export const useGetFeed = ({
  queryParams,
  getLimit,
  swrOptions,
}: UseGetFeedParams = {}) => {
  const { cache } = useSWRConfig();

  const queryKey = getFeedQueryKey(queryParams);

  useEffect(() => {
    cache.delete(queryKey);
  }, [cache, queryKey]);

  const { data, ...swrResponse } = useSWR<GetFeedResponse>(
    queryKey,
    () =>
      getFeed({
        queryParams,
        limit: getLimit?.(),
      }),
    swrOptions,
  );

  return {
    data,
    ...swrResponse,
  };
};
